import * as React from 'react';
import { Box, Drawer as MuiDrawer } from '@mui/material';

import { ReactComponent as CloseIcon } from 'assets/images/close-icon.svg';
import { NAV_DRAWER_WIDTH, NAV_DRAWER_WIDTH_LARGE } from 'domain/consts/ui';
import { Container, ContainerItem, Divider, Header, Text, Tooltip } from 'ui/atoms';

interface Props {
  open: boolean;
  onClose: () => void;
  anchor?: 'left' | 'right';
  width?: 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';
  bottomAction?: React.ReactNode;
  bottomActionMargin?: string;
  dataCy?: string;
  hideBackdrop?: boolean;
  id?: string;
  permanent?: boolean;
  subtitle?: string;
  title?: string;
  children?: React.ReactNode;
}

const widthMap = {
  xsmall: { xs: NAV_DRAWER_WIDTH_LARGE, lg: NAV_DRAWER_WIDTH }, // used for the nav drawers
  small: { xs: '100%', sm: '384px' },
  medium: { xs: '100%', sm: '482px' },
  large: { xs: '100%', sm: '600px' },
  xlarge: { xs: '100%', md: '754px' } // sm breakpoint is 600px, so need to use md breakpoint of 1024px for xlarge so that the drawer doesn't overflow.
};

export const Drawer: React.FC<Props> = ({
  open,
  onClose,
  anchor = 'right',
  width = 'medium',
  bottomAction,
  bottomActionMargin,
  dataCy,
  hideBackdrop,
  id,
  permanent,
  subtitle,
  title,
  children
}) => {
  const drawerWidth = widthMap[width];
  const subtitleMaxLength = 36;

  return (
    <MuiDrawer
      anchor={anchor}
      data-cy={dataCy}
      hideBackdrop={hideBackdrop}
      id={id}
      onClose={onClose}
      open={open}
      variant={permanent ? 'permanent' : 'temporary'}
      sx={{
        flexShrink: 0,
        width: drawerWidth,

        '& .MuiDrawer-paper': {
          borderLeft: (theme) => (anchor === 'right' ? `1px solid ${theme.palette.grey[400]}` : 'none'),
          borderRight: (theme) => (anchor === 'left' ? `1px solid ${theme.palette.grey[400]}` : 'none'),
          height: '100vh',
          overflow: 'hidden',
          width: drawerWidth
        }
      }}
      ModalProps={{ disableEnforceFocus: true }} // this prevents stealing focus from inputs, ie when the chat widget is opened over a drawer
    >
      {anchor === 'right' && (
        <Container align="center" padding="0" width="100%">
          <Container width="100%" padding="16px">
            <ContainerItem width="70%" grow padding="0">
              {title && (
                <Tooltip title={title} disabled={title.length <= (Number(width) > 600 ? 60 : 27)}>
                  <Header dataCy="drawer-title" type="h2" content={title} textOverflow="ellipsis" />
                </Tooltip>
              )}
              {subtitle && (
                <Tooltip title={subtitle} disabled={subtitle.length < subtitleMaxLength + 1}>
                  <Text
                    dataCy="drawer-subtitle"
                    content={
                      subtitle
                        ? subtitle.length > subtitleMaxLength
                          ? subtitle.substring(0, subtitleMaxLength - 1) + '...'
                          : subtitle
                        : ''
                    }
                    sx={{
                      color: (theme) => theme.palette.text.secondary,
                      fontSize: '16px',
                      pt: '4px',
                      width: '90%'
                    }}
                    textOverflow="ellipsis"
                  />
                </Tooltip>
              )}
            </ContainerItem>
            {!permanent && (
              <ContainerItem flex padding="0">
                <CloseIcon data-cy="drawer-close" onClick={onClose} style={{ cursor: 'pointer' }} />
              </ContainerItem>
            )}
          </Container>

          <ContainerItem flex margin={'0 -24px'} padding="0" width="calc(100% + 48px)">
            <Divider />
          </ContainerItem>
        </Container>
      )}

      {bottomAction ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
          <Box
            sx={{
              overflow: 'auto',
              width: drawerWidth,
              flex: '1 1 auto'
            }}
          >
            {children}
          </Box>

          <Box
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              margin: bottomActionMargin || '0',
              width: drawerWidth,
              flexShrink: 0
            }}
          >
            <ContainerItem textAlign="center" padding="0" width="100%">
              <Box
                sx={{
                  padding: '0.5rem 0',
                  width: '100%'
                }}
              >
                <Divider />
              </Box>

              {bottomAction}
            </ContainerItem>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            overflowY: 'auto',
            overflowX: 'hidden',
            width: '100%'
          }}
        >
          <Box
            sx={{
              display: 'block',
              width: '100%'
            }}
          >
            {children}
          </Box>
        </Box>
      )}
    </MuiDrawer>
  );
};
